import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Battery, ShieldCheck, Clock, ArrowRight, ChevronsUpDown, MessageCircle, Phone,
} from "lucide-react";
import {
  CATEGORIES, CATEGORY_LABEL, BATTERY_SUBTYPES,
  TUBULAR_MODELS, THREE_WHEELER_MODELS,
  HUPS_SUBTYPES, HUPS_APPLICATIONS, hupsModels,
  isCategoryComingSoon, COMING_SOON_NOTE,
  LUBE_FAMILIES, LUBE_PRODUCTS,
} from "../lib/catalog";
import {
  brandsFor, modelsFor, fuelsFor as fitFuelsFor, optionsFor,
  preloadFitments, WARRANTY_FOOTNOTE as FITMENT_WARRANTY_FOOTNOTE,
} from "../lib/fitment";

// --- Battery type → dynamic (fitment) vs. special (direct list / KVA) --------
// Two of the six top-level battery types have entries that don't fit the
// brand → model cascade: e-rickshaws (traction batteries, no vehicle chart)
// and gensets (rated by KVA). Both get treated as "coming soon — Amaron is
// listening" so the lead can still convert.
const DYNAMIC_SUBTYPES = new Set([
  "two_wheeler", "automotive", "three_wheeler",
  "tractor", "commercial", "spv_genset",
]);

const persistPreselect = (adapted) => {
  try {
    sessionStorage.setItem("bbh_preselect", JSON.stringify({
      product_category: adapted.category,
      selected_brand: adapted.brand_series,
      selected_model: adapted.model_code,
    }));
  } catch { /* storage unavailable */ }
};

export default function BatterySelector({ onSelect }) {
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState("");
  const [subtype, setSubtype] = useState("");
  const [f, setF] = useState({});   // per-non-battery-flow scratch state
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Auto-select category via ?category=hups etc. from Products page.
  useEffect(() => {
    const preset = (searchParams.get("category") || "").toLowerCase();
    if (["batteries", "hups", "lubricants", "solar"].includes(preset) && !category) {
      setCategory(preset);
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="battery-selector-card"]');
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // --- Batteries flow state (fitment-driven) ---------------------------------
  const [brand, setBrand] = useState("");
  const [vModel, setVModel] = useState("");
  const [fuel, setFuel] = useState("");
  const [brandsData, setBrandsData] = useState(null);        // { all, popular, grouped }
  const [modelList, setModelList] = useState([]);            // string[]
  const [fuelList, setFuelList] = useState([]);              // string[]
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingFuels, setLoadingFuels] = useState(false);

  // Warm the fitment cache the moment the customer picks a dynamic battery type.
  useEffect(() => {
    if (!subtype || !DYNAMIC_SUBTYPES.has(subtype)) return;
    preloadFitments();
  }, [subtype]);

  // Load brands whenever a dynamic battery subtype is selected.
  useEffect(() => {
    setBrand(""); setVModel(""); setFuel("");
    setModelList([]); setFuelList([]); setResult(null); setError("");
    if (category !== "batteries" || !subtype || !DYNAMIC_SUBTYPES.has(subtype)) {
      setBrandsData(null);
      return;
    }
    let cancelled = false;
    setLoadingBrands(true);
    brandsFor(subtype)
      .then((data) => { if (!cancelled) setBrandsData(data); })
      .catch((err) => { if (!cancelled) { setBrandsData(null); setError(err.message || "Could not load brands."); } })
      .finally(() => { if (!cancelled) setLoadingBrands(false); });
    return () => { cancelled = true; };
  }, [category, subtype]);

  // Load models whenever the brand changes.
  useEffect(() => {
    setVModel(""); setFuel(""); setFuelList([]); setResult(null);
    if (!brand || category !== "batteries" || !DYNAMIC_SUBTYPES.has(subtype)) { setModelList([]); return; }
    let cancelled = false;
    setLoadingModels(true);
    modelsFor(subtype, brand)
      .then((data) => { if (!cancelled) setModelList(data); })
      .catch(() => { if (!cancelled) setModelList([]); })
      .finally(() => { if (!cancelled) setLoadingModels(false); });
    return () => { cancelled = true; };
  }, [brand, subtype, category]);

  // Load fuels whenever the model changes. If there's only one fuel option
  // (or none), auto-set it and skip the step — asking a question with one
  // answer is a wasted tap.
  useEffect(() => {
    setFuel(""); setResult(null);
    if (!vModel || category !== "batteries" || !DYNAMIC_SUBTYPES.has(subtype)) { setFuelList([]); return; }
    let cancelled = false;
    setLoadingFuels(true);
    fitFuelsFor(subtype, brand, vModel)
      .then((data) => {
        if (cancelled) return;
        setFuelList(data);
        if (data.length <= 1) setFuel(data[0] || "—");
      })
      .catch(() => { if (!cancelled) setFuelList([]); })
      .finally(() => { if (!cancelled) setLoadingFuels(false); });
    return () => { cancelled = true; };
  }, [vModel, brand, subtype, category]);

  const needsFuelStep = fuelList.length > 1;

  // ── Fitment-driven Recommend ──────────────────────────────────────────────
  const runFitmentRecommend = async () => {
    setError("");
    try {
      const opts = await optionsFor(subtype, brand, vModel, fuel);
      const fuelLabel = fuel && fuel !== "—" ? ` · ${fuel}` : "";
      if (!opts.length) {
        showComingSoon({
          heading: `${brand} ${vModel}${fuelLabel}`,
          subtitle: "Amaron is listening",
          application: `We couldn't find a factory-listed Amaron battery for this vehicle yet. Share your details and we will personally recommend the closest fit.`,
        });
        return;
      }
      const primary = opts[0];
      const adapted = {
        category: subtype,
        brand_series: primary.seriesLabel,
        model_code: primary.code || primary.part,
        capacity: primary.capacity || "As per spec",
        warranty: primary.warranty || "",
        application: `${brand} ${vModel}${fuelLabel}`,
        price_range: "On request",
        _options: opts,
        _fitmentHeading: `Suitable Batteries For ${brand.toUpperCase()} ${vModel}${fuelLabel}`,
        _selection: { category, subtype, brand, vModel, fuel },
      };
      setResult(adapted);
      onSelect?.(adapted);
      persistPreselect(adapted);
    } catch (e) {
      setError(e.message || "Could not load recommendations.");
    }
  };

  const showComingSoon = ({ heading, subtitle, application }) => {
    const adapted = {
      category: subtype || category,
      brand_series: heading || "Amaron",
      model_code: "Coming Soon",
      capacity: "—",
      warranty: "",
      application: application || COMING_SOON_NOTE,
      price_range: "Coming Soon",
      _comingSoon: true,
      _subtitle: subtitle,
      _selection: { category, subtype, ...f },
    };
    setResult(adapted); onSelect?.(adapted); persistPreselect(adapted);
  };

  // ── Non-battery flows: gating & recommend (unchanged from previous impl) ──
  const solarComingSoon = isCategoryComingSoon("solar") && category === "solar";

  const canRecommendBatteries = category === "batteries"
    && DYNAMIC_SUBTYPES.has(subtype)
    && brand && vModel
    && (!needsFuelStep || fuel);

  const canRecommendOther = useMemo(() => {
    if (category === "hups") {
      if (f.hups_subtype === "tubular_batteries") return Boolean(f.model_code);
      if (f.hups_subtype === "ups_units") return Boolean(f.application_type && f.model_code);
      return false;
    }
    if (category === "lubricants") return Boolean(f.lube_family && f.model_code);
    return false;
  }, [category, f]);

  const CanRecommend = canRecommendBatteries || canRecommendOther;

  const recommend = async () => {
    if (canRecommendBatteries) return runFitmentRecommend();

    if (category === "hups" && f.hups_subtype === "tubular_batteries" && f.model_code) {
      const picked = TUBULAR_MODELS.find((m) => m.code === f.model_code);
      if (picked) {
        const adapted = {
          category: "tubular_batteries",
          brand_series: `Amaron ${picked.series.split("—")[0].trim()}`,
          model_code: picked.code,
          capacity: `${picked.ah} Ah`,
          warranty: picked.code.startsWith("AAM-CR-AMS") ? "72 Months" : "36-60 Months (varies by model)",
          application: `Inverter Battery · ${picked.series}`,
          price_range: "On request",
          _selection: { category, subtype, ...f },
        };
        setResult(adapted); onSelect?.(adapted); persistPreselect(adapted);
      }
      return;
    }
    if (category === "hups" && f.hups_subtype === "ups_units" && f.application_type && f.model_code) {
      const list = hupsModels(f.application_type);
      const picked = list.find((m) => m.code === f.model_code);
      if (picked) {
        const upsWarranty = f.application_type === "commercial_ups" ? "24 Months Warranty*" : "36 Months Warranty*";
        const adapted = {
          category: "ups_units",
          brand_series: `Amaron Pro ${picked.series}`,
          model_code: picked.code,
          capacity: picked.va,
          warranty: upsWarranty,
          application: `${HUPS_APPLICATIONS.find((a) => a.value === f.application_type)?.label || f.application_type} · ${picked.series}`,
          price_range: "On request",
          _selection: { category, subtype, ...f },
        };
        setResult(adapted); onSelect?.(adapted); persistPreselect(adapted);
      }
      return;
    }
    if (category === "lubricants" && f.lube_family && f.model_code) {
      const sku = (LUBE_PRODUCTS[f.lube_family] || []).find((p) => p.code === f.model_code);
      const familyLabel = LUBE_FAMILIES.find((x) => x.value === f.lube_family)?.label || f.lube_family;
      if (!sku) {
        showComingSoon({ heading: familyLabel, subtitle: "Amaron is listening",
          application: "We could not find this SKU in the price list. Register interest and we will get back to you." });
        return;
      }
      const adapted = {
        category: "lubricants",
        brand_series: `Amaron Hi-Life ${familyLabel}`,
        model_code: sku.code,
        capacity: `${sku.pack} ${sku.unit}`,
        warranty: sku.grade || sku.name || "—",
        application: familyLabel,
        price_range: "On request",
        _selection: { category, ...f },
      };
      setResult(adapted); onSelect?.(adapted); persistPreselect(adapted);
    }
  };

  return (
    <div className="relative">
      <div className="grain absolute inset-0 rounded-2xl pointer-events-none" />
      <Card data-testid="battery-selector-card" className="relative bg-card/80 backdrop-blur-xl border-black/10 p-6 md:p-8 rounded-2xl ring-accent-soft">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Product Selector</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-1">Find your perfect Amaron product</h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">Powered by the official Amaron fitment chart — 1,600+ vehicle models.</p>
          </div>
          <Battery className="hidden md:block h-10 w-10 text-secondary bolt-glow" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* 1. Product Category */}
          <FieldSelect
            label="Product Category"
            value={category}
            onChange={(v) => { setCategory(v); setSubtype(""); setF({}); setResult(null); setError(""); }}
            testid="sel-category"
            options={CATEGORIES.map((c) => ({
              value: c.value,
              label: c.value === "hups" ? "HUPS / Inverter Battery (Tubular)" : c.label,
            }))}
          />

          {/* 2. Battery Type (batteries only) — six values, exactly as before */}
          {category === "batteries" && (
            <FieldSelect
              label="Battery Type"
              value={subtype}
              onChange={(v) => { setSubtype(v); setResult(null); }}
              testid="sel-subtype"
              options={BATTERY_SUBTYPES.map((s) => ({ value: s.value, label: s.label }))}
            />
          )}

          {/* 3. Vehicle Brand — grouped: common brands first, then all alphabetically */}
          {category === "batteries" && DYNAMIC_SUBTYPES.has(subtype) && (
            <GroupedBrandSelect
              label="Vehicle Brand"
              value={brand}
              onChange={setBrand}
              testid="sel-veh-brand"
              grouped={brandsData?.grouped || []}
              loading={loadingBrands}
            />
          )}

          {/* 4. Vehicle Model — searchable/type-to-filter combobox */}
          {category === "batteries" && DYNAMIC_SUBTYPES.has(subtype) && brand && (
            <SearchableSelect
              label="Vehicle Model"
              value={vModel}
              onChange={setVModel}
              testid="sel-veh-model"
              options={modelList}
              disabled={!brand || loadingModels}
              loading={loadingModels}
              placeholder="Type to search…"
              emptyText="No models found."
            />
          )}

          {/* 5. Fuel — only when there are 2 or more options */}
          {category === "batteries" && DYNAMIC_SUBTYPES.has(subtype) && vModel && needsFuelStep && (
            <FieldSelect
              label="Fuel"
              value={fuel}
              onChange={setFuel}
              testid="sel-fuel"
              options={fuelList.map((v) => ({ value: v, label: v }))}
            />
          )}

          {/* HUPS product line — unchanged */}
          {category === "hups" && (
            <>
              <FieldSelect
                label="Product Type"
                value={f.hups_subtype || ""}
                onChange={(v) => setF({ hups_subtype: v })}
                testid="sel-hups-type"
                options={HUPS_SUBTYPES.map((s) => ({ value: s.value, label: s.label }))}
              />
              {f.hups_subtype === "ups_units" && (
                <>
                  <FieldSelect
                    label="Application"
                    value={f.application_type || ""}
                    onChange={(v) => setF((prev) => ({ ...prev, application_type: v, model_code: "" }))}
                    testid="sel-hups-app"
                    options={HUPS_APPLICATIONS.map((a) => ({ value: a.value, label: a.label }))}
                  />
                  <FieldSelect
                    label="Capacity"
                    value={f.model_code || ""}
                    onChange={(v) => setF((prev) => ({ ...prev, model_code: v }))}
                    testid="sel-hups-cap"
                    disabled={!f.application_type}
                    options={hupsModels(f.application_type).map((m) => ({ value: m.code, label: `${m.code} · ${m.va} · ${m.series}` }))}
                  />
                </>
              )}
              {f.hups_subtype === "tubular_batteries" && (
                <FieldSelect
                  label="Inverter Battery Model"
                  value={f.model_code || ""}
                  onChange={(v) => setF((prev) => ({ ...prev, model_code: v }))}
                  testid="sel-tubular-model"
                  options={TUBULAR_MODELS.map((m) => ({ value: m.code, label: `${m.code} · ${m.ah} Ah · ${m.series}` }))}
                />
              )}
            </>
          )}

          {/* Lubricants — unchanged */}
          {category === "lubricants" && (
            <>
              <FieldSelect
                label="Lubricant Family"
                value={f.lube_family || ""}
                onChange={(v) => setF({ lube_family: v })}
                testid="sel-lube-family"
                options={LUBE_FAMILIES.map((x) => ({ value: x.value, label: x.label }))}
              />
              <FieldSelect
                label="SKU"
                value={f.model_code || ""}
                onChange={(v) => setF((prev) => ({ ...prev, model_code: v }))}
                testid="sel-lube-sku"
                disabled={!f.lube_family}
                options={(LUBE_PRODUCTS[f.lube_family] || []).map((p) => ({
                  value: p.code,
                  label: `${p.code} · ${p.grade || p.name || ""} · ${p.pack} ${p.unit}`.trim(),
                }))}
              />
            </>
          )}
        </div>

        {solarComingSoon && <ComingSoonNote message={COMING_SOON_NOTE} />}

        {error && (
          <div className="mt-4 text-xs text-red-600" data-testid="selector-error">{error}</div>
        )}

        <div className="mt-6 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {loadingBrands ? "Loading brands…" : loadingModels ? "Loading models…" : loadingFuels ? "Checking fuel options…" : (brandsData?.all?.length ? `${brandsData.all.length} brands · Amaron chart` : "")}
          </div>
          <Button
            data-testid="btn-recommend"
            disabled={!CanRecommend}
            onClick={recommend}
            className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white disabled:opacity-40 px-6"
          >
            Recommend Product <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>

        {/* --- Result --------------------------------------------------------- */}
        {result && (
          <div className="mt-8 relative animate-fade-up">
            {/* Coming-soon block */}
            {result._comingSoon && (
              <Card className="bg-card border rounded-2xl p-6 md:p-8 border-secondary/40 ring-1 ring-secondary/20" data-testid="result-card-coming-soon">
                <div className="flex items-center gap-2 mb-2">
                  <span className="pulse-dot h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">
                    Coming Soon{result._subtitle ? ` — ${result._subtitle}` : ""}
                  </span>
                </div>
                <h4 className="font-display text-3xl md:text-4xl font-black">{result.brand_series}</h4>
                <p className="mt-3 text-sm text-foreground/80 max-w-2xl">{result.application}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href="/find-battery/request" data-testid="btn-request-quote">
                    <Button className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white px-6">
                      <ShieldCheck className="h-4 w-4 mr-1.5" /> Request a Call Back
                    </Button>
                  </a>
                </div>
              </Card>
            )}

            {/* Fitment results — one card per option, longest warranty first */}
            {!result._comingSoon && result._options && (
              <FitmentResults heading={result._fitmentHeading} options={result._options} onRequest={(subj) => {
                const adapted = { ...result, model_code: subj };
                onSelect?.(adapted); persistPreselect(adapted);
              }} />
            )}

            {/* Non-battery flows (HUPS / Lubricants) — single-card result */}
            {!result._comingSoon && !result._options && (
              <Card className="bg-card border rounded-2xl p-6 md:p-8 border-primary/30 ring-accent-glow" data-testid="result-card">
                <div className="flex items-center gap-2 mb-2">
                  <span className="pulse-dot h-2 w-2 rounded-full bg-secondary" />
                  <span className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Recommended For You</span>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{CATEGORY_LABEL[result.category] || result.category}</div>
                    <h4 className="font-display text-3xl md:text-4xl font-black">{result.brand_series}</h4>
                    <div className="mt-1 text-sm text-secondary font-semibold">{result.model_code}</div>
                    <p className="mt-4 text-sm text-foreground/80">{result.application}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 content-start">
                    <Stat label={result.category === "lubricants" ? "Pack" : "Capacity"} value={result.capacity} />
                    {result.warranty ? <Stat label="Warranty" value={result.warranty} /> : null}
                    <Stat label="Price Range" value={result.price_range || "On request"} />
                    <Stat label="Availability" value={<Badge className="bg-primary/20 text-secondary hover:bg-primary/25 border border-primary/40">In Stock</Badge>} />
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href="/find-battery/request" data-testid="btn-request-quote">
                    <Button className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white px-6">
                      <ShieldCheck className="h-4 w-4 mr-1.5" /> Request Price / Availability
                    </Button>
                  </a>
                </div>
              </Card>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// --- FitmentResults — one card per option, plus footnote ---------------------
function FitmentResults({ heading, options, onRequest }) {
  return (
    <div data-testid="fitment-results">
      {heading && (
        <div className="text-xs uppercase tracking-[0.16em] font-bold text-foreground/70 mb-4" data-testid="fitment-heading">
          {heading}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {options.map((o, idx) => (
          <Card
            key={`${o.code || o.part}-${idx}`}
            data-testid={`fitment-option-${idx}`}
            className={`bg-card border rounded-2xl p-5 md:p-6 ${idx === 0 ? "border-primary/40 ring-1 ring-primary/20" : "border-black/10"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              {idx === 0 && <Badge className="bg-primary/20 text-secondary hover:bg-primary/25 border border-primary/40 text-[10px]">Longest Warranty</Badge>}
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Option {idx + 1}</span>
            </div>
            <h4 className="font-display text-2xl md:text-3xl font-black">{o.seriesLabel}</h4>
            <div className="mt-1 font-mono text-sm text-secondary font-semibold" data-testid={`fitment-part-${idx}`}>{o.part}</div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              {o.capacity && <span className="text-foreground/80"><b>Capacity:</b> {o.capacity}</span>}
              {o.warranty && (
                <span className="text-foreground/80" data-testid={`fitment-warranty-${idx}`}>
                  <b>Warranty:</b> {o.warranty}
                </span>
              )}
            </div>
            {o.note && (
              <p className="mt-3 text-xs text-muted-foreground italic">{o.note}</p>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={o.enquiry.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid={`fitment-whatsapp-${idx}`}
              >
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white">
                  <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp Enquiry
                </Button>
              </a>
              <a
                href="/find-battery/request"
                onClick={() => onRequest?.(o.enquiry.subject)}
                data-testid={`fitment-callback-${idx}`}
              >
                <Button variant="outline" className="rounded-full border-black/15">
                  <Phone className="h-4 w-4 mr-1.5" /> Request a Call Back
                </Button>
              </a>
            </div>
          </Card>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground italic" data-testid="warranty-footnote">
        {FITMENT_WARRANTY_FOOTNOTE}
      </p>
    </div>
  );
}

// --- Field primitives --------------------------------------------------------
function FieldSelect({ label, value, onChange, options, testid, disabled }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={testid} className="mt-2 bg-muted/40 border-black/10 h-12 rounded-md">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent className="bg-card border-black/10 max-h-[320px]">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Choose the previous field first</div>
          ) : options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Grouped brand dropdown: renders each { label, items } block with a heading.
// Uses shadcn Select's built-in SelectGroup + SelectLabel, so the "Common in
// Sikar" pin group shows up above the alphabetical "All brands" list.
function GroupedBrandSelect({ label, value, onChange, testid, grouped, loading }) {
  // Flatten every brand across groups and render one clean alphabetical list.
  // Popular-brand pinning is intentionally dropped — customers scanning a
  // dropdown expect a-z, and mixing groups slowed people down more than it
  // helped.
  const seen = new Set();
  const all = grouped
    .flatMap((g) => g.items)
    .filter((n) => (seen.has(n) ? false : seen.add(n)))
    .sort((a, b) => a.localeCompare(b));
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange} disabled={loading || !all.length}>
        <SelectTrigger data-testid={testid} className="mt-2 bg-muted/40 border-black/10 h-12 rounded-md">
          <SelectValue placeholder={loading ? "Loading…" : "Select…"} />
        </SelectTrigger>
        <SelectContent className="bg-card border-black/10 max-h-[380px]">
          {all.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Choose the battery type first</div>
          ) : all.map((name) => (
            <SelectItem key={name} value={name} data-testid={`${testid}-${name.replace(/\s+/g, "-").toLowerCase()}`}>{name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Searchable combobox for the model list — Maruti Suzuki alone has 126 models,
// so type-to-filter is the only usable interaction on mobile.
function SearchableSelect({ label, value, onChange, options, testid, disabled, loading, placeholder = "Type to search…", emptyText = "No results" }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            disabled={disabled}
            className="mt-2 w-full h-12 bg-muted/40 border border-black/10 rounded-md px-3 flex items-center justify-between text-sm disabled:opacity-40"
          >
            <span className={value ? "text-foreground" : "text-muted-foreground"}>
              {loading ? "Loading…" : value || "Select…"}
            </span>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card border-black/10" align="start">
          <Command shouldFilter>
            <CommandInput placeholder={placeholder} data-testid={`${testid}-input`} />
            <CommandList className="max-h-[280px]">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem
                    key={o}
                    value={o}
                    onSelect={(v) => { onChange(v); setOpen(false); }}
                    data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-black/10 rounded-lg p-4 bg-black/[0.03]">
      <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-bold">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function ComingSoonNote({ message }) {
  return (
    <div className="mt-5 rounded-xl border border-secondary/30 bg-secondary/5 p-4 flex items-start gap-3" data-testid="coming-soon-note">
      <Clock className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-display font-bold text-sm text-foreground">Coming soon</div>
        <p className="text-xs text-muted-foreground mt-1">{message}</p>
        <a href="/find-battery/request" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-secondary underline underline-offset-4">
          Notify me / request a call back <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}
